<script lang="ts">
  import {
    activitiesFor, ARTIFACTS, ARTIFACT_CATEGORY_LABELS, ARTIFACT_PITY_RIFTS, BUILDING_SUPPORT_RESOURCES_BY_SKILL,
    artifactCategory, artifactDustCost, artifactRarity, artifactSetTier, contractObjectiveProgress, contractObjectives, contractProgress, contractProgressRatio, PRODUCTION_SURGE_MULTIPLIER, rewardDescription,
    type ActivityActionResult, type ArtifactCategory, type ContractObjective, type GameState, type RewardToken
  } from '@rsvjelle/game-core';
  import { formatNumber, titleCase } from '../lib/display.ts';

  interface Props {
    gameState: GameState;
    onClaim: (contractId: string) => ActivityActionResult;
    onUseReward: (rewardId: string) => ActivityActionResult;
    onOpenRift: () => ActivityActionResult;
    onCraft: (artifactId: string) => ActivityActionResult;
  }
  let { gameState, onClaim, onUseReward, onOpenRift, onCraft }: Props = $props();
  let activities = $derived(activitiesFor(gameState));
  let artifactIds = $derived(new Set(activities.artifacts));
  type ArtifactFilter = 'all' | 'found' | 'missing' | ArtifactCategory;
  let artifactFilter = $state<ArtifactFilter>('all');
  let selectedArtifactId = $state<string | null>(null);
  let visibleArtifacts = $derived(ARTIFACTS.filter((artifact) => {
    if (artifactFilter === 'found') return artifactIds.has(artifact.id);
    if (artifactFilter === 'missing') return !artifactIds.has(artifact.id);
    if (['combat', 'gathering', 'artisan', 'adventure', 'realm'].includes(artifactFilter)) return artifactCategory(artifact) === artifactFilter;
    return true;
  }));
  let selectedArtifact = $derived(ARTIFACTS.find((artifact) => artifact.id === selectedArtifactId));
  const collectionCategories: Exclude<ArtifactCategory, 'realm'>[] = ['combat', 'gathering', 'artisan', 'adventure'];

  function rewardIcon(reward: RewardToken): string {
    if (reward.kind === 'goldCache') return '🪙';
    if (reward.kind === 'supplyCache') return '📦';
    return '⚡';
  }

  function scrollToVault(): void {
    document.getElementById('reward-vault')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function supplyDetails(reward: RewardToken): string {
    const materials = BUILDING_SUPPORT_RESOURCES_BY_SKILL[gameState.run.activeSkill];
    const each = reward.amount / materials.length;
    return `${formatNumber(each)} ${titleCase(materials[0])} + ${formatNumber(each)} ${titleCase(materials[1])} for the ${gameState.run.activeSkill} Hall`;
  }

  function objectiveAmount(objective: ContractObjective, amount: number): string {
    if (objective.metric === 'activeSkillSeconds' || objective.metric === 'surgeSeconds') return `${formatNumber(amount / 60, 1)}m`;
    return formatNumber(amount, 0);
  }
</script>

<section class="view contracts-view" aria-labelledby="contracts-title">
  <div class="section-heading">
    <div><p class="eyebrow">ORDERS BEYOND ONE LIFETIME</p><h2 id="contracts-title">Realm Contracts</h2></div>
    <button class="vault-count" type="button" onclick={scrollToVault} aria-label={`Go to Reward Vault, ${activities.rewardVault.length} rewards stored`}>🎁 {activities.rewardVault.length}</button>
  </div>
  <p class="section-copy">Contract progress and sealed rewards survive every Ascension. Reward values are fixed by their Tier when generated; claim them into the Vault and activate them whenever they matter most.</p>

  {#if activities.productionBoostSeconds > 0}
    <div class="surge-banner"><span>⚡</span><div><strong>Production Surge active</strong><small>×{PRODUCTION_SURGE_MULTIPLIER} XP and resources · {formatNumber(activities.productionBoostSeconds / 60, 1)} minutes remaining</small></div></div>
  {/if}

  {#if activities.dormantRift}
    <button class="dormant-rift" type="button" onclick={onOpenRift}><span>◈</span><div><strong>A Dormant Rift followed you home</strong><small>Open the single Rift preserved during offline progress.</small></div><b>OPEN</b></button>
  {/if}

  <div class="contract-grid">
    {#each activities.contracts as contract}
      {@const progress = contractProgress(gameState, contract)}
      {@const objectives = contractObjectives(contract)}
      {@const progressRatio = contractProgressRatio(gameState, contract)}
      <article class:complete={contract.completed} class="contract-card">
        <div class="contract-top"><span>{contract.category ?? 'Realm'} · Tier {contract.tier}</span><strong>{contract.completed ? 'Complete' : objectives.length > 1 ? `${formatNumber(progressRatio * 100, 0)}%` : `${formatNumber(progress, 0)} / ${formatNumber(contract.target, 0)}`}</strong></div>
        <h3>{contract.title}</h3><p>{contract.description}</p>
        <div class="contract-progress"><span style={`width:${Math.min(100, progressRatio * 100)}%`}></span></div>
        <div class="contract-objectives">
          {#each objectives as objective}
            {@const objectiveProgress = contractObjectiveProgress(gameState, objective)}
            <div class:done={objectiveProgress >= objective.target}><span>{objectiveProgress >= objective.target ? '✓' : '○'}</span><p>{objective.label}</p><strong>{objectiveAmount(objective, objectiveProgress)} / {objectiveAmount(objective, objective.target)}</strong></div>
          {/each}
        </div>
        <div class="contract-reward"><span>{rewardIcon(contract.reward)}</span><div><small>SEALED REWARD</small><strong>{rewardDescription(contract.reward)}</strong></div></div>
        <button type="button" disabled={!contract.completed} onclick={() => onClaim(contract.id)}>{contract.completed ? 'Store in Reward Vault' : 'Contract in progress'}</button>
      </article>
    {/each}
  </div>

  <div class="subsection-heading" id="reward-vault"><div><p class="eyebrow">ASCENSION-SAFE INVENTORY</p><h3>Reward Vault</h3></div><small>Stored separately · fixed value · use one at a time</small></div>
  {#if activities.rewardVault.length === 0}
    <div class="empty-vault"><span>🔒</span><strong>Your Vault is empty</strong><small>Complete a Contract or open a Rift to store a reward.</small></div>
  {:else}
    <div class="reward-vault-grid">
      {#each activities.rewardVault as reward (reward.id)}
        <article><span>{rewardIcon(reward)}</span><div><small>TIER {reward.tier}</small><strong>{rewardDescription(reward)}</strong>{#if reward.kind === 'supplyCache'}<p>{supplyDetails(reward)}</p>{/if}</div><button type="button" onclick={() => onUseReward(reward.id)}>Use now</button></article>
      {/each}
    </div>
  {/if}

  <div class="subsection-heading"><div><p class="eyebrow">RIFT DISCOVERIES</p><h3>Artifact Codex</h3></div><small>{activities.artifacts.length}/{ARTIFACTS.length} found · pity {activities.artifactMisses ?? 0}/{ARTIFACT_PITY_RIFTS}</small></div>
  <div class="artifact-dust-balance"><span>✨</span><div><small>ARTIFACT DUST</small><strong>{formatNumber(activities.artifactDust ?? 0, 0)}</strong></div><p>Duplicates become Dust. Reconstruct any missing silhouette when you have enough.</p></div>
  <div class="artifact-sets">
    {#each collectionCategories as category}
      {@const found = ARTIFACTS.filter((artifact) => artifactCategory(artifact) === category && artifactIds.has(artifact.id)).length}
      {@const tier = artifactSetTier(gameState, category)}
      <article class:active={tier > 0}><strong>{ARTIFACT_CATEGORY_LABELS[category]}</strong><span>{found} found</span><small>{tier === 2 ? '5-piece active · +2% XP & production' : tier === 1 ? '3-piece active · find 5 for +2%' : `Find ${Math.max(0, 3 - found)} more · +1% XP & production`}</small></article>
    {/each}
  </div>
  <div class="artifact-filters" aria-label="Filter artifacts">
    {#each [['all', 'All'], ['found', 'Found'], ['missing', 'Missing'], ['combat', 'Combat'], ['gathering', 'Gathering'], ['artisan', 'Artisan'], ['adventure', 'Adventure'], ['realm', 'Realm']] as option}
      <button class:active={artifactFilter === option[0]} type="button" onclick={() => artifactFilter = option[0] as ArtifactFilter}>{option[1]}</button>
    {/each}
  </div>
  <div class="artifact-grid">
    {#each visibleArtifacts as artifact}
      <button class:found={artifactIds.has(artifact.id)} class:rare={artifactRarity(artifact) === 'rare'} class:mythic={artifactRarity(artifact) === 'mythic'} class:selected={selectedArtifactId === artifact.id} type="button" onclick={() => selectedArtifactId = artifact.id} aria-label={artifactIds.has(artifact.id) ? artifact.name : `Undiscovered ${artifactRarity(artifact)} artifact`} title={artifactIds.has(artifact.id) ? artifact.name : `Undiscovered ${artifactRarity(artifact)} artifact`}>
        <span class:unknown={!artifactIds.has(artifact.id)}>{artifact.icon}</span>
      </button>
    {/each}
  </div>
  {#if selectedArtifact}
    <article class:found={artifactIds.has(selectedArtifact.id)} class:rare={artifactRarity(selectedArtifact) === 'rare'} class:mythic={artifactRarity(selectedArtifact) === 'mythic'} class="artifact-detail">
      <span class:unknown={!artifactIds.has(selectedArtifact.id)}>{selectedArtifact.icon}</span>
      <div><small>{artifactIds.has(selectedArtifact.id) ? `${artifactRarity(selectedArtifact).toUpperCase()} · ${ARTIFACT_CATEGORY_LABELS[artifactCategory(selectedArtifact)].toUpperCase()}` : `UNDISCOVERED ${artifactRarity(selectedArtifact).toUpperCase()} · ${ARTIFACT_CATEGORY_LABELS[artifactCategory(selectedArtifact)].toUpperCase()}`}</small><h4>{artifactIds.has(selectedArtifact.id) ? selectedArtifact.name : `Unknown ${artifactRarity(selectedArtifact)} Artifact`}</h4><p>{artifactIds.has(selectedArtifact.id) ? selectedArtifact.description : 'Its identity and effect remain hidden until it is found or reconstructed.'}</p></div>
      {#if !artifactIds.has(selectedArtifact.id)}
        {@const cost = artifactDustCost(selectedArtifact)}
        <button class="craft-artifact" type="button" disabled={(activities.artifactDust ?? 0) < cost} onclick={() => onCraft(selectedArtifact.id)}>Reconstruct · {formatNumber(cost, 0)} Dust</button>
      {/if}
    </article>
  {/if}
</section>
